import { type Components, defaultUrlTransform } from "react-markdown";
import { AppointmentDocumentLink } from "./AppointmentDocumentLink";
import { amaGuideHandler } from "./handlers/amaGuide";
import { amaGuideSchemeHandler } from "./handlers/amaGuideScheme";
import { appointmentDocumentHandler } from "./handlers/appointmentDocument";
import { externalLinkHandler } from "./handlers/externalLink";
import { internalRouteHandler } from "./handlers/internalRoute";
import type { ChatbotLinkContext, ChatbotLinkHandler } from "./types";

/**
 * Default handler set for the chatbot. Ordering matters — the first
 * `matches(href)` wins, so more specific handlers go first and the
 * external catch-all goes last.
 *
 * Scheme handlers (`indigo://…`) run first so backend-emitted links go
 * straight to their dedicated viewers. The Table/Figure handler then
 * picks up canonical fragment anchors (`#table-16-3`) before the
 * generic internal-route and external fallbacks.
 */
export const DEFAULT_CHATBOT_LINK_HANDLERS: ChatbotLinkHandler[] = [
  amaGuideSchemeHandler,
  appointmentDocumentHandler,
  amaGuideHandler,
  internalRouteHandler,
  externalLinkHandler,
];

/**
 * react-markdown's default `urlTransform` strips any scheme outside its
 * safe allowlist (http, https, mailto, tel, irc/ircs, xmpp), which would
 * otherwise empty out our `indigo://` hrefs before the handler registry
 * sees them. Pass through `indigo:` explicitly, delegate the rest to the
 * stock sanitizer so we don't open up javascript:/data: hrefs in
 * LLM-authored text.
 */
export function chatbotUrlTransform(url: string): string {
  if (url.startsWith("indigo:")) return url;
  return defaultUrlTransform(url);
}

/**
 * Run every handler's optional `linkify` pass over the raw markdown,
 * in order. Later passes see the output of earlier ones, so handlers
 * that produce anchors (e.g. AMA) should run before any pass that
 * consumes anchors.
 */
export function applyLinkifiers(
  markdown: string,
  handlers: ChatbotLinkHandler[] = DEFAULT_CHATBOT_LINK_HANDLERS,
): string {
  if (!markdown) return markdown;
  let out = markdown;
  for (const h of handlers) {
    if (h.linkify) out = h.linkify(out);
  }
  return out;
}

/** Find the first handler that claims the given href, or `null`. */
export function resolveLinkHandler(
  href: string,
  handlers: ChatbotLinkHandler[] = DEFAULT_CHATBOT_LINK_HANDLERS,
): ChatbotLinkHandler | null {
  for (const h of handlers) {
    if (h.matches(href)) return h;
  }
  return null;
}

/**
 * Build the react-markdown `a` component for chatbot messages. Dispatches
 * clicks through the handler registry; unrecognized hrefs fall through to
 * a safe external-link default (new tab, `noopener,noreferrer`) so we
 * preserve the pre-registry behavior.
 */
export function buildChatbotLinkComponent(
  ctx: ChatbotLinkContext,
  handlers: ChatbotLinkHandler[] = DEFAULT_CHATBOT_LINK_HANDLERS,
): NonNullable<Components["a"]> {
  return ({ href, children, ...rest }) => {
    if (!href) {
      return <a {...rest}>{children}</a>;
    }
    const handler = resolveLinkHandler(href, handlers);
    if (!handler) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
          {children}
        </a>
      );
    }
    if (handler.id === "appointment-document") {
      return (
        <AppointmentDocumentLink href={href} ctx={ctx} {...rest}>
          {children}
        </AppointmentDocumentLink>
      );
    }
    return (
      <a
        href={href}
        data-chatbot-link-handler={handler.id}
        onClick={(e) => {
          // Let the user open in a new tab / new window with modifiers,
          // matching standard browser behavior for <a> elements.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          const result = handler.onClick(href, ctx);
          // Async handlers (or future ones that add awaits) shouldn't have
          // their rejections disappear into `void`. Log in dev so broken
          // plumbing surfaces during development without spamming users
          // in production; swap to Sentry / error-boundary hook if one
          // gets wired up later.
          if (result instanceof Promise) {
            result.catch((err) => {
              if (process.env.NODE_ENV !== "production") {
                console.error(
                  `[chatbotLinks] handler ${handler.id} failed:`,
                  err,
                );
              }
            });
          }
        }}
        {...rest}
      >
        {children}
      </a>
    );
  };
}
